import { cleanClass } from '@shared/utils/cleanClass.js';
import { forwardRef, useEffect, useRef } from 'react';

const ResizableTextArea = forwardRef(({ id, autoFocus, className, autoComplete, onFocus, onKeyUp, onBlur, disabled, type, placeholder, onChange, onKeyDown, value, defaultValue, maxLength, size, borderless = false, style, autoSize, maxHeight, rows }, ref) => {
  const internalRef = useRef(null);
  const textareaRef = ref || internalRef;
  // Uncontrolled when a `defaultValue` is supplied instead of a `value`: the DOM
  // owns the text, so typing triggers no React re-render. Callers read it through
  // the ref on submit. Controlled callers (no defaultValue) are unaffected.
  // Latch the mode on first render so a caller that lets defaultValue drift
  // between undefined and a string never flips the element controlled state,
  // which React warns about and which desyncs the DOM value.
  const isControlledRef = useRef(defaultValue === undefined);
  const isControlled = isControlledRef.current;

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
  };

  const handleKeyUp = (e) => {
    onKeyUp?.(e);
  };

  const adjustTextareaHeight = (textarea) => {
    if (!textarea) {
      return;
    }
    // Temporarily disable transition and reset height to measure true scrollHeight
    textarea.style.transition = 'none';
    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    // Force a reflow so the browser registers the reset, then re-enable transition
    textarea.offsetHeight; // eslint-disable-line no-unused-expressions
    textarea.style.transition = '';
    if (maxHeight && newHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
  };

  const handleChange = (e) => {
    if (type === 'textarea' && autoSize) {
      adjustTextareaHeight(e.target);
    }
    onChange?.(e);
  };

  useEffect(() => {
    if (autoSize && textareaRef?.current) {
      adjustTextareaHeight(textareaRef.current);
    }
  }, [autoSize, maxHeight, value]);

  useEffect(() => {
    if (autoSize && textareaRef?.current) {
      const timeoutId = setTimeout(() => {
        adjustTextareaHeight(textareaRef.current);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  return (
    <textarea
      className={cleanClass(`comp-textarea ${size || ''} ${className || ''} ${borderless ? 'borderless' : ''}`)} //
      style={style}
      name={id}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={placeholder}
      spellCheck={true}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      maxLength={maxLength}
      {...(isControlled ? { value: value || '' } : { defaultValue: defaultValue || '' })}
      ref={textareaRef}
      rows={autoSize ? undefined : rows || 1}
    />
  );
});
ResizableTextArea.displayName = 'ResizableTextArea';

export default ResizableTextArea;
